import React, { useState, useRef, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import EmojiPicker from 'emoji-picker-react';
import chatService from '../../../services/chatService';
import paperclipIcon from '../../../assets/Paperclip.svg';
import emojiIcon from '../../../assets/Emoji.svg';
import sendIcon from '../../../assets/Send.svg';



/**
 * MessageInput Component
 * Input area for composing and sending messages
 *
 * @param {Object} props
 * @param {function} props.onSend - Handler for sending message
 * @param {function} props.onTypingStart - Handler for typing started
 * @param {function} props.onTypingStop - Handler for typing stopped
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.placeholder - Placeholder text
 */
const MessageInput = ({
  onSend,
  onTypingStart,
  onTypingStop,
  disabled = false,
  placeholder = 'Type a message'
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [message]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Handle typing indicator
  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      onTypingStart?.();
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing after 3 seconds
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      onTypingStop?.();
    }, 3000);
  };

  const handleChange = (e) => {
    setMessage(e.target.value);
    handleTyping();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage && attachments.length === 0) {
      return;
    }

    onSend?.({
      body: trimmedMessage,
      attachments: attachments
    });

    // Reset state
    setMessage('');
    setAttachments([]);
    setIsTyping(false);
    onTypingStop?.();

    // Clear typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed the limit
    if (attachments.length + files.length > 10) {
      console.warn('Maximum 10 attachments allowed');
      return;
    }

    e.target.value = ''; // Reset file input immediately

    // Upload each file to get temp upload ID
    for (const file of files) {
      const tempAttachment = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploading: true,
        tempId: null,
        error: null
      };

      // Add to list immediately with uploading state
      setAttachments(prev => [...prev, tempAttachment]);

      try {
        // Upload to temp storage using chatService
        const response = await chatService.uploadTempFile(file);

        // Backend returns: { status: true, data: { id: 5, ... } }
        if (response.data?.id) {
          // Update attachment with temp upload ID
          setAttachments(prev => prev.map(att =>
            att.file === file ? {
              ...att,
              uploading: false,
              tempId: response.data.id
            } : att
          ));
        } else {
          throw new Error(response.message || 'Upload failed');
        }
      } catch (error) {
        console.error('File upload error:', error);
        // Mark attachment as failed
        setAttachments(prev => prev.map(att =>
          att.file === file ? {
            ...att,
            uploading: false,
            error: error.message || 'Upload failed'
          } : att
        ));
      }
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleEmojiClick = () => {
    setShowEmojiPicker(prev => !prev);
  };

  const handleEmojiSelect = (emojiData) => {
    setMessage(prev => prev + emojiData.emoji);
    textareaRef.current?.focus();
  };

  const canSend = (message.trim().length > 0 || attachments.filter(att => att.tempId).length > 0)
    && !attachments.some(att => att.uploading);

  return (
    <div className="talk-message-input-container">
      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="talk-attachment-previews">
          {attachments.map((attachment, index) => (
            <div key={index} className={`talk-attachment-preview ${attachment.uploading ? 'uploading' : ''} ${attachment.error ? 'error' : ''}`}>
              <div className="talk-attachment-preview-content">
                <span className="talk-attachment-preview-icon">
                  {attachment.error ? '❌' : '📎'}
                </span>
                <div className="talk-attachment-preview-info">
                  <div className="talk-attachment-preview-name">
                    {attachment.name}
                  </div>
                  <div className="talk-attachment-preview-size">
                    {attachment.error
                      ? <span className="text-error">{attachment.error}</span>
                      : `${(attachment.size / 1024).toFixed(1)} KB`
                    }
                  </div>
                </div>
              </div>
              <button
                className="talk-attachment-preview-remove"
                onClick={() => handleRemoveAttachment(index)}
                aria-label={`Remove ${attachment.name}`}
                type="button"
                disabled={attachment.uploading}
              >
                <FiX size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="talk-emoji-picker-container" ref={emojiPickerRef}>
          <EmojiPicker
            onEmojiClick={handleEmojiSelect}
            width="100%"
            height="400px"
            searchDisabled={false}
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
          />
        </div>
      )}

      {/* Input area */}
      <div className="talk-message-input-wrapper">
        <button
          className="talk-input-action-btn"
          onClick={handleEmojiClick}
          aria-label="Add emoji"
          title="Emoji"
          type="button"
          disabled={disabled}
        >
          <img src={emojiIcon} alt="Emoji" width="28" height="28" />
        </button>

        <button
          className="talk-input-action-btn"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach file"
          title="Attach file"
          type="button"
          disabled={disabled}
        >
          <img src={paperclipIcon} alt="Attach" width="28" height="28" />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="*/*"
          disabled={disabled}
        />

        <textarea
          ref={textareaRef}
          className="talk-message-input"
          placeholder={placeholder}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (isTyping) {
              setIsTyping(false);
              onTypingStop?.();
            }
          }}
          disabled={disabled}
          rows={1}
          aria-label="Message input"
        />

        <button
          className={`talk-send-btn ${canSend ? 'talk-send-btn-active' : ''}`}
          onClick={handleSend}
          disabled={!canSend || disabled}
          aria-label="Send message"
          title="Send"
          type="button"
        >
          <img src={sendIcon} alt="Send" width="28" height="28" className="talk-send-icon" />
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
